<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-2xl font-bold mt-2">Your Progress</h1>
      </div>
    </header>

    <main class="max-w-4xl mx-auto px-4 py-8">
      <div v-if="loading" class="text-center py-16 text-gray-500">Loading…</div>
      <div v-else-if="error" class="text-center py-16">
        <p class="text-red-500">{{ error }}</p>
        <button
          type="button" @click="loadProgress"
          class="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >Try again</button>
      </div>

      <template v-else>
        <!-- Summary cards -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-8">
          <div v-for="card in cards" :key="card.label" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
            <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ card.value }}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ card.label }}</div>
          </div>
        </div>

        <!-- Activity heatmap -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">Activity (last {{ weeks }} weeks)</h2>
          <div class="flex gap-1 overflow-x-auto pb-2">
            <div v-for="(week, wi) in grid" :key="wi" class="flex flex-col gap-1">
              <div
                v-for="cell in week" :key="cell.date"
                class="w-3 h-3 rounded-sm"
                :class="cellClass(cell.xp)"
                :title="`${cell.date}: ${cell.xp} XP`"
              ></div>
            </div>
          </div>
          <div class="flex items-center gap-2 mt-3 text-xs text-gray-400">
            <span>Less</span>
            <span class="w-3 h-3 rounded-sm bg-gray-100 dark:bg-gray-700"></span>
            <span class="w-3 h-3 rounded-sm bg-emerald-200"></span>
            <span class="w-3 h-3 rounded-sm bg-emerald-300"></span>
            <span class="w-3 h-3 rounded-sm bg-emerald-500"></span>
            <span class="w-3 h-3 rounded-sm bg-emerald-700"></span>
            <span>More</span>
          </div>
        </div>

        <!-- Achievements -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mt-6">
          <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
            Achievements ({{ earnedCount }}/{{ achievements.length }})
          </h2>
          <div class="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 gap-3">
            <div
              v-for="a in achievements" :key="a.key"
              class="flex items-center gap-3 rounded-xl border p-3"
              :class="a.earned
                ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/40'
                : 'border-gray-200 dark:border-gray-700 opacity-60'"
            >
              <span class="text-2xl" :class="a.earned ? '' : 'grayscale'">{{ a.icon }}</span>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug break-words">{{ a.title }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ a.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet } from '../api/client'

const weeks = 18
const loading = ref(true)
const error = ref(null)
const summary = ref({})
const xpByDate = ref({})
const achievements = ref([])
const earnedCount = computed(() => achievements.value.filter(a => a.earned).length)

const cards = computed(() => {
  const s = summary.value
  return [
    { label: 'Day streak', value: `🔥 ${s.current_streak ?? 0}` },
    { label: 'Longest streak', value: s.longest_streak ?? 0 },
    { label: 'Level', value: s.level ?? 1 },
    { label: 'Total XP', value: s.total_xp ?? 0 },
    { label: 'Words learned', value: s.words_learned ?? 0 },
    { label: 'Accuracy', value: `${s.accuracy_pct ?? 0}%` },
    { label: 'Streak freezes', value: `❄️ ${s.streak_freezes ?? 0}` },
  ]
})

function cellClass(xp) {
  if (!xp) return 'bg-gray-100 dark:bg-gray-700'
  if (xp <= 20) return 'bg-emerald-200'
  if (xp <= 50) return 'bg-emerald-300'
  if (xp <= 100) return 'bg-emerald-500'
  return 'bg-emerald-700'
}

// Local components, NOT toISOString(): the grid's Date objects are local
// midnight, and toISOString converts to UTC first — so at any positive offset
// (Vietnam +7, Australia +10) every cell was labelled the previous day and
// stopped matching the server's keys, which are computed in the user's own
// timezone. Today's square showed yesterday's XP.
function isoDate(d) {
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

// Build a GitHub-style grid: columns = weeks (Sun→Sat), ending today.
const grid = computed(() => {
  const today = new Date()
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const start = new Date(end)
  start.setDate(end.getDate() - (weeks * 7 - 1))
  start.setDate(start.getDate() - start.getDay()) // back to Sunday

  const columns = []
  let cursor = new Date(start)
  while (cursor <= end) {
    const week = []
    for (let i = 0; i < 7; i++) {
      const ds = isoDate(cursor)
      week.push({ date: ds, xp: cursor <= end ? (xpByDate.value[ds] || 0) : 0 })
      cursor.setDate(cursor.getDate() + 1)
    }
    columns.push(week)
  }
  return columns
})

onMounted(loadProgress)

// Named so the error state can retry without a page reload.
async function loadProgress() {
  loading.value = true
  error.value = null
  try {
    const [data, ach] = await Promise.all([
      apiGet('/api/v1/progress/activity?days=126'),
      apiGet('/api/v1/achievements').catch(() => ({ achievements: [] })),
    ])
    summary.value = data.summary || {}
    const map = {}
    ;(data.days || []).forEach(d => { map[d.date] = d.xp })
    xpByDate.value = map
    achievements.value = ach.achievements || []
  } catch (e) {
    error.value = 'Could not load your progress.'
  } finally {
    loading.value = false
  }
}
</script>
